import {
  IconDocument,
  IconHome,
  IconInference,
  IconJobAutomation,
  IconModel,
  IconRegistries,
  IconRocketLaunch,
} from '@wandb/weave/components/Icon';
import React, {useContext} from 'react';

import {useIsAutomationHubEnabled} from '../../../components/Automations/hooks/useIsAutomationHubEnabled';
import {usePublicQueueAccess} from '../../../components/Launch/hooks';
import {useAccountOrganizationOrPersonalEntityUsingLaunch} from '../../../components/Launch/launchActive';
import {useIsGlobalRegistryEnabled} from '../../../components/Registries/common/hooks';
import {AccountSelectorContext} from '../../../components/Search/SearchNav/AccountSelectorContextProvider';
import {AccountType} from '../../../components/Search/SearchNav/types';
import {useSelectedAccountOrgScope} from '../../../components/UserSettings/selectedAccountOrgScope';
import {envIsServerDeployment} from '../../../config';
import {
  trackGlobalRegistryClicked,
  trackModelRegistryClicked,
  trackProfileMenuClicked,
} from '../../../util/navigation';
import {
  useRampFlagInferenceCatalog,
  useRampFlagMarimoNotebookManagementWithLoading,
  useRampFlagMarimoNotebooksWithLoading,
} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import * as urls from '../../../util/urls';
import {useRegistryAccess} from '../../../util/useModelsAccess';
import {useHomePageContext} from '../HomePageContextProvider';
import * as S from './HomePageSidebar.styles';
import {ProjectsList} from './ProjectsList';
import {SidebarLink} from './SidebarLink';
import {SidebarSectionHeader} from './SidebarSectionHeader';
import {TeamsList} from './TeamsList';
import {TrialCard} from './TrialCard';

export const HomePageSidebar = () => {
  const {
    homePageViewerDetailsQuery: {data: viewerData, refetch: refetchViewerData},
  } = useHomePageContext();

  const {enableGlobalRegistry} = useIsGlobalRegistryEnabled();
  const {hasRegistryAccess} = useRegistryAccess();

  const {isLaunchActive, error: isLaunchActiveError} =
    useAccountOrganizationOrPersonalEntityUsingLaunch();
  const {hasPublicQueueAccess} = usePublicQueueAccess();

  const {selectedAccount} = useContext(AccountSelectorContext);
  const {isAutomationHubEnabled} = useIsAutomationHubEnabled();
  const {isAccountListLoading, organizationId} = useSelectedAccountOrgScope();
  const {isEnabled: isManagementEnabled, loading: isManagementLoading} =
    useRampFlagMarimoNotebookManagementWithLoading(organizationId ?? null);
  const {isEnabled: isListingEnabled, loading: isListingLoading} =
    useRampFlagMarimoNotebooksWithLoading();
  const isNotebooksNavLoading =
    isAccountListLoading || isManagementLoading || isListingLoading;
  const isNotebooksNavEnabled =
    !isNotebooksNavLoading &&
    organizationId != null &&
    isManagementEnabled === true &&
    isListingEnabled === true;
  const showCoreSection =
    viewerData?.username != null &&
    (hasRegistryAccess || isNotebooksNavEnabled);

  const isPersonalAccount =
    selectedAccount?.accountType === AccountType.Personal;
  const isNonServerDeploymentPersonalAccount =
    !envIsServerDeployment && isPersonalAccount;
  const isInferenceRampEnabled = useRampFlagInferenceCatalog(
    selectedAccount?.name ?? ''
  );
  const isCatalogLinkShown =
    isInferenceRampEnabled || isNonServerDeploymentPersonalAccount;

  if (isLaunchActiveError) {
    console.error('Error getting account or personal entity using launch');
  }

  return (
    <div
      data-test="home-page-sidebar"
      className={`[ul>*:p-0] flex h-full flex-col`}>
      <div className="mb-16">
        <SidebarLink
          dataTest="home-page-sidebar-header"
          icon={<IconHome />}
          labelText="Home"
          url={urls.homePage()}
        />
      </div>
      <ProjectsList />
      {showCoreSection && (
        <div className="mb-16">
          <SidebarSectionHeader headerText="Core" />
          {hasRegistryAccess && (
            <>
              {enableGlobalRegistry ? (
                <SidebarLink
                  dataTest="global-registry"
                  icon={<IconRegistries />}
                  labelText="Registry"
                  url={urls.registries()}
                  isActive={urls.isOnRegistriesPage}
                  onClick={() => {
                    trackGlobalRegistryClicked('Home sidebar');
                  }}
                />
              ) : (
                <SidebarLink
                  dataTest="model-registry"
                  icon={<IconModel />}
                  labelText="Model registry"
                  url={urls.objectRegistry('model')}
                  isActive={urls.isOnModelRegistriesPage}
                  onClick={() => {
                    trackModelRegistryClicked('Home sidebar');
                  }}
                />
              )}
              {(isLaunchActive || hasPublicQueueAccess) && (
                <SidebarLink
                  icon={<IconRocketLaunch />}
                  labelText="Launch"
                  url={urls.launchOverview()}
                />
              )}
              {isCatalogLinkShown && (
                <SidebarLink
                  icon={<IconInference />}
                  labelText="Inference"
                  url={urls.inferenceModels()}
                />
              )}
              {isAutomationHubEnabled && (
                <SidebarLink
                  icon={<IconJobAutomation />}
                  labelText="Automations"
                  url={urls.homeAutomations()}
                />
              )}
            </>
          )}
          {isNotebooksNavEnabled && (
            <SidebarLink
              icon={<IconDocument />}
              labelText="Marimo notebooks"
              url={urls.homeNotebooks()}
            />
          )}
        </div>
      )}
      {viewerData?.username && (
        <div className="mb-16">
          <SidebarSectionHeader headerText="Profile" />
          <SidebarLink
            key={viewerData.username}
            icon={
              <S.AvatarImage
                alt="Home nav profile avatar"
                src={viewerData.photoUrl}
              />
            }
            labelText={viewerData.username}
            url={urls.profilePage(viewerData.username)}
            onClick={() => {
              trackProfileMenuClicked('Home sidebar');
            }}
          />
        </div>
      )}
      {!isPersonalAccount && (
        <TeamsList
          name="Teams"
          data={viewerData?.teams ?? {edges: []}}
          userQueryRefetch={refetchViewerData}
        />
      )}
      {/* Forces Sticky Footer positioning */}
      <div className="flex flex-auto flex-col justify-end">
        <div className="sticky bottom-16">
          {!envIsServerDeployment && <TrialCard />}
        </div>
      </div>
    </div>
  );
};

export default HomePageSidebar; // displayName
